import React, { useEffect } from "react";
import {
  Phone,
  Mail,
  CalendarDays,
  CreditCard,
  ExternalLink,
} from "lucide-react";
import Cal, { getCalApi } from "@calcom/embed-react";

const CAL_LINK = "michael-marlow-5ryzj8/secret";
const CAL_DIRECT_URL =
  "https://cal.com/michael-marlow-5ryzj8/secret?month=2026-05";

const Booking = () => {
  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "deepclean" });
      cal("ui", {
        theme: "light",
        hideEventTypeDetails: false,
        layout: "month_view",
      });
    })();
  }, []);

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative bg-slate-950 py-20 text-white md:py-32"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.25), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.30), transparent)",
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="mb-6 font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
            — Book Your Cleaning
          </p>
          <h2
            data-testid="booking-heading"
            className="font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl"
          >
            Pick a time.
            <br />
            <span className="text-cyan-300">We'll handle the rest.</span>
          </h2>
          <p className="mt-6 max-w-md font-body text-base font-medium leading-relaxed text-slate-300 md:text-lg">
            Choose a day and time below for a walkthrough or first service.
            You'll get an instant confirmation and a calendar invite.
          </p>

          <ul className="mt-10 space-y-5 border-t border-white/15 pt-8">
            <li className="flex items-start gap-4">
              <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white/10">
                <CalendarDays size={18} strokeWidth={1.8} className="text-cyan-300" />
              </span>
              <div>
                <p className="font-heading text-base font-bold text-white">Custom service plans</p>
                <p className="font-body text-sm text-slate-300">Daily, weekly, or monthly — built for your traffic.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white/10">
                <CreditCard size={18} strokeWidth={1.8} className="text-cyan-300" />
              </span>
              <div>
                <p className="font-heading text-base font-bold text-white">Convenient payment options</p>
                <p className="font-body text-sm text-slate-300">Card, ACH, or invoice — whatever works for your business.</p>
              </div>
            </li>
            <li>
              <a href="tel:9102369971" data-testid="booking-call-link" className="group flex items-start gap-4">
                <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white/10 transition-colors group-hover:bg-blue-600">
                  <Phone size={18} strokeWidth={1.8} className="text-cyan-300 group-hover:text-white" />
                </span>
                <div>
                  <p className="font-heading text-base font-bold text-white">910-236-9971</p>
                  <p className="font-body text-sm text-slate-300 group-hover:text-cyan-200">Prefer to talk? Tap to call us directly.</p>
                </div>
              </a>
            </li>
            <li>
              <a href="mailto:info@deepcleanrr.com" data-testid="booking-email-link" className="group flex items-start gap-4">
                <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white/10 transition-colors group-hover:bg-blue-600">
                  <Mail size={18} strokeWidth={1.8} className="text-cyan-300 group-hover:text-white" />
                </span>
                <div>
                  <p className="font-heading text-base font-bold text-white">info@deepcleanrr.com</p>
                  <p className="font-body text-sm text-slate-300 group-hover:text-cyan-200">We reply within 1 business day</p>
                </div>
              </a>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div
            data-testid="booking-cal-wrapper"
            className="relative overflow-auto rounded-md border border-slate-200 bg-white text-slate-900"
            style={{ height: 820 }}
          >
            <Cal
              namespace="deepclean"
              calLink={CAL_LINK}
              style={{ width: "100%", height: "100%", minHeight: "820px" }}
              config={{ layout: "month_view", theme: "light" }}
              data-testid="booking-cal-embed"
            />
          </div>
          <p className="mt-4 text-center font-body text-xs text-slate-400">
            Having trouble loading?{" "}
            <a
              href={CAL_DIRECT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-cal-fallback-link"
              className="inline-flex items-center gap-1 font-semibold text-cyan-300 hover:underline"
            >
              Open the booking page in a new tab
              <ExternalLink size={11} strokeWidth={2.2} />
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Booking;
